<template>
    <nav class="tab-bar">
        <div class="tab-bar-inner" ref="wrapper">
            <template v-if="scrollable">
                <div class="tab-bar-start" v-show="maskStartShown"></div>
                <div class="tab-bar-end" v-show="maskEndShown"></div>
            </template>
            <scroll-view
                ref="scroller"
                :scrolling-x="scrollable"
                :scrolling-y="false"
                :key="scrollerTmpKey"
                @scroll="$_onScroll"
            >
                <div class="tab-bar-list" :style="{width: contentW + 'px'}">
                    <a
                        class="tab-bar-item"
                        :class="{
                            'is-active': currentName === item.name,
                            'is-disabled': !!item.disabled
                        }"
                        v-for="(item, index) in items"
                        :key="item.name"
                        ref="items"
                        @click="$_onClick(item, index)"
                    >
                        <slot
                            name="item"
                            :item="item"
                            :items="items"
                            :index="index"
                            :currentName="currentName"
                        >
                            {{ item.label }}
                        </slot>
                    </a>
                </div>
                <span
                    class="tab-bar-ink"
                    :class="{
                        'is-disabled': currentTab && currentTab.disabled
                    }"
                    v-if="hasInk"
                    :style="{
                        width: inkWidth + 'px',
                        transform: 'translateX(' + inkPos + 'px)',
                    }"
                ></span>
            </scroll-view>
        </div>
    </nav>
</template>

<script>
    import ScrollView from '../ScrollView'

    export default {
        name: 'TabBar',

        components: {
            [ScrollView.name]: ScrollView,
        },

        props: {
            value: {
                type: [String, Number],
                default: '',
            },
            items: {
                type: Array,
                default: () => [],
            },
            hasInk: {
                type: Boolean,
                default: true,
            },
            inkLength: {
                type: Number,
                default: 100,
            },
            immediate: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                currentName: '',
                wrapperW: 0,
                contentW: 0,
                inkWidth: 0,
                inkPos: 0,
                scrollerTmpKey: Date.now(),
                maskStartShown: false,
                maskEndShown: true,
            }
        },

        computed: {
            scrollable() {
                return this.contentW > this.wrapperW
            },
            currentIndex() {
                for (let i = 0, len = this.items.length; i < len; i++) {
                    if (this.items[i].name === this.currentName) {
                        return i
                    }
                }
                return false;
            },
            currentTab() {
                if (this.currentIndex) {
                    return this.items[this.currentIndex]
                }

                return false;
            },
        },

        watch: {
            value: {
                immediate: true,
                handler(val) {
                    if (val !== this.currentName) {
                        this.currentName = val
                    }
                },
            },
            inkWidth() {
                this.$nextTick(function() {
                    this.reflow()
                })
            },
            items() {
                this.$nextTick(function() {
                    this.reflow()
                })
            },
            currentIndex() {
                this.$nextTick(function() {
                    this.reflow()
                })
            },
            scrollable() {
                this.scrollerTmpKey = Date.now()
            },
        },

        created() {
            if (this.currentName === '' && this.items.length) {
                this.currentName = this.items[0].name
                this.$emit('change', this.items[0], 0, 0)
            }
        },
        mounted() {
            this.$_resizeEnterBehavior()
        },
        activated() {
            this.$_resizeEnterBehavior()
        },
        deactivated() {
            this.$_resizeLeaveBehavior()
        },
        beforeDestroy() {
            this.$_resizeLeaveBehavior()
        },

        methods: {
            $_onScroll({scrollLeft}){
                if (scrollLeft > 0) {
                    this.maskStartShown = true
                } else {
                    this.maskStartShown = false
                }

                if (scrollLeft >= this.$refs.scroller.contentW - this.$refs.scroller.containerW) {
                    this.maskEndShown = false
                } else {
                    this.maskEndShown = true
                }
            },
            $_onClick(item, index) {
                if (item.disabled) {
                    return
                }
                this.$emit('change', item, index, this.currentIndex)
                this.currentName = item.name
                this.$emit('input', item.name)
            },
            $_resizeEnterBehavior() {
                window.addEventListener('resize', this.reflow)
                this.reflow()
                /* istanbul ignore next */
                if (this.immediate) {
                    this.$nextTick(() => {
                        this.$emit('change', this.items[this.currentIndex], this.currentIndex)
                    })
                }
            },
            $_resizeLeaveBehavior() {
                window.removeEventListener('resize', this.reflow)
            },
            reflow() {
                if (!this.$refs.items || this.$refs.items.length === 0) {
                    return
                }

                const wrapperReact = this.$refs.wrapper.getBoundingClientRect()
                this.wrapperW = wrapperReact.width

                let contentWidth = 0
                for (let i = 0, len = this.items.length; i < len; i++) {
                    const {width} = this.$refs.items[i].getBoundingClientRect()
                    contentWidth += width
                }
                this.contentW = contentWidth
                this.$refs.scroller.reflowScroller()
                this.$nextTick(() => {
                    if (!this.$refs.items || !this.$refs.items[this.currentIndex]) {
                        return
                    }

                    const target = this.$refs.items[this.currentIndex]
                    this.inkWidth = target.offsetWidth * this.inkLength / 200;
                    this.inkPos = target.offsetLeft + (target.offsetWidth - this.inkWidth) / 2

                    const prevTarget = this.$refs.items[this.currentIndex - 1]
                    const nextTarget = this.$refs.items[this.currentIndex + 1]

                    if (!prevTarget) {
                        this.$refs.scroller.scrollTo(0, 0, true)
                        return
                    }
                    if (!nextTarget) {
                        this.$refs.scroller.scrollTo(this.contentW, 0, true)
                        return
                    }

                    const wrapperRect = this.$refs.wrapper.getBoundingClientRect()
                    const prevTargetRect = prevTarget.getBoundingClientRect()
                    const nextTargetRect = nextTarget.getBoundingClientRect()

                    if (prevTargetRect && prevTargetRect.left < wrapperRect.left) {
                        this.$refs.scroller.scrollTo(prevTarget.offsetLeft, 0, true)
                    } else if (nextTargetRect && nextTargetRect.right > wrapperRect.right) {
                        this.$refs.scroller.scrollTo(nextTarget.offsetLeft + nextTarget.offsetWidth - this.wrapperW, 0, true)
                    }
                })
            },
        },
    }
</script>

<style lang="scss" scoped>
    .tab-bar {
        position: relative;
        // border-bottom:1px solid #e5e5e5;
    }
    .tab-bar-inner {
        position: relative;
        width: 100%;

    }
    .tab-bar-list {
        height: 96px;
        min-width: 100%;
    }
    .tab-bar-item {
        position: relative;
        float: left;
        color: #333;
        font-size: 24px;
        font-weight: 400;
        line-height: 96px;
        padding: 0 20px;
        margin: 0 auto;
        box-sizing: border-box;
        -webkit-user-select: none;
        -webkit-tap-highlight-color: transparent;

        &.is-active {
            font-size: 32px;
            font-weight: 600;
        }

        &.is-disabled {
            color: gray;
        }
    }
    .tab-bar-ink {
        position: absolute;
        bottom: 0;
        left: 0;
        display: block;
        width: 60px;
        height: 8px;
        background-color: #D91313;
        border-radius: 4px;

        &.is-disabled {
            background-color: gray;
        }
    }
    .tab-bar-start,
    .tab-bar-end {
        position: absolute;
        top: 0;
        left: 0;
        width: 14px;
        overflow: hidden;

        &::after {
            content: '';
            display: block;
            position: absolute;
            left: -14px;
            top: 50%;
            width: 14px;
            border-radius: 50%;
            box-shadow: -1px 0 12px 0 rgba(0, 0, 0, .2);
        }
    }
    .tab-bar-end {
        left: auto;
        right: 0;
        transform: rotate(180deg);
    }
    .tab-bar {
        .scroll-view {
            display: block;
        }
        .scroll-view-container {
            min-width: 100%;
        }
    }
</style>